import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import UndoIcon from "@mui/icons-material/Undo";
import { BaseTextFieldProps, InputBaseComponentProps, InputProps } from "@mui/material";
import { green } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { alpha, styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Text from "_shared/text/text";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, JSX, useCallback, useEffect } from "react";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalIncomingChangeHighlight from "work/proposals/negotiation/proposal-incoming-change-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import DiffText from "work/proposals/redlining/__diff/_views/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import ResolutionTargetAuthorLabel from "work/proposals/redlining/field-redlines/_views/resolution-target-author-label";

const RedlineTextBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
  width: "min-content",
  "& .Mui-disabled div span": {
    webkitTextFillColor: "revert",
    opacity: "0.75",
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TextContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "disableFullWidth" && prop !== "showTextInput" && prop !== "bold"
})<{ bold?: boolean; disableFullWidth?: boolean; showTextInput: boolean;  }>(({ theme, bold, disableFullWidth, showTextInput }) => ({
  pointerEvents: showTextInput ? "none" : "auto",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  // Anchors the transient incoming-change tint to this field's value. Positioning alone changes no
  // geometry, so the field lays out exactly as it did before.
  position: "relative",
  paddingRight: theme.spacing(2),
  width: disableFullWidth ? "unset" : "100%",
  fontWeight: bold ? "500" : "normal",
}));
const InputContainer = styled("span")(() => ({
  "&.MuiInputBase-input": {
    display: "flex",
    flexDirection: "column",
    height: "unset",
  },
}));
const ResolvedValue = styled(Typography)(() => ({
  color: "inherit",
  fontWeight: "inherit",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));
const InputField = styled(Input, {
  shouldForwardProp: (prop) => prop !== "show",
})<{ show: boolean }>(({ show, theme }) => ({
  height: show ? "unset" : 0,
  margin: theme.spacing(2, 0, 1),
  width: show ? "100%" : 0,
  overflow: show ? "visible" : "hidden",
  padding: 0,
  pointerEvents: show ? "auto" : "none",
  position: show ? "relative" : "absolute",
  "& .MuiInputBase-input": {
    padding: 0,
  },
}));

interface InputSubComponentProps extends InputBaseComponentProps {
  textRedline: FieldRedline<Text>;
  issues?: ProposalIssue[];
  hideAcceptRejectButtons: boolean;
  hideUndoButton: boolean;
  readOnly?: boolean;
  disableFullWidth?: boolean;
  hideFieldHistory?: boolean;
  /** See `RedlineTextInputProps.hideHistoryBadge`. */
  hideHistoryBadge?: boolean;
  negotiationId?: string;
  viewerScope?: string;
  focused: boolean;
  showPlaceholder?: boolean;
  outerRef?: React.RefObject<HTMLDivElement>;
  bold?: boolean;
  animateAcceptedResolved?: boolean;
  onAcceptedResolvedAnimationStart?: () => void;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => Text;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onActionButtonsChange?: (actionButtons: JSX.Element) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const InputSubComponent = forwardRef<HTMLInputElement, InputSubComponentProps>(function InputSubComponent(
  { outerRef, ...props },
  forwardedRef
) {
  const {
    className,
    textRedline,
    issues,
    hideAcceptRejectButtons,
    hideUndoButton,
    hideFieldHistory,
    hideHistoryBadge,
    negotiationId,
    viewerScope,
    readOnly,
    disableFullWidth,
    focused,
    bold,
    animateAcceptedResolved,
    onAcceptedResolvedAnimationStart,
    fieldConstructor,
    onTraverseIn,
    onTextFieldRedlineChange,
    onActionButtonsChange,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput
  } = props as InputSubComponentProps;

  const getBaseValue = React.useCallback(() => {
    return textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "";
  }, [textRedline]);

  const [draftTextValue, setDraftTextValue] = React.useState<string>(
    textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? ""
  );
  const [showTextInput, setShowTextInput] = React.useState<boolean>(false);

  const acceptRejectButtons = React.useMemo(() => {
    if (hideAcceptRejectButtons || textRedline.isResolved) return null;

    return (
      <>
        <Tooltip title="Accept Change">
          <span>
            <ActionButton
              aria-label="Accept Change"
              onClick={() => {
                const newRedline = textRedline.accept();
                onAcceptedResolvedAnimationStart?.();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CheckIcon color="success" />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Reject Change">
          <span>
            <ActionButton
              aria-label="Reject Change"
              onClick={() => {
                const newRedline = textRedline.reject();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CloseIcon color="error" />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [textRedline, onTextFieldRedlineChange, hideAcceptRejectButtons, onAcceptedResolvedAnimationStart]);

  const undoButton = React.useMemo(() => {
    if (hideUndoButton || !textRedline.canBeUndone) return null;

    return (
      <Tooltip title="Undo Changes">
        <span>
          <ActionButton
            aria-label="Undo Changes"
            onClick={() => {
              const newRedline = textRedline.undo();
              onTextFieldRedlineChange(newRedline, newRedline.field);
            }}
          >
            <UndoIcon color="secondary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [textRedline, onTextFieldRedlineChange, hideUndoButton]);

  const editButton = React.useMemo(() => {
    if (readOnly) return null;

    return (
      <Tooltip title="Edit">
        <span>
          <ActionButton
            aria-label="Edit"
            onClick={() => {
              setDraftTextValue(getBaseValue());
              setShowTextInput(true);
              onTraverseIn?.();
              onShowTextInput?.();
            }}
          >
            <EditNoteIcon color="primary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [readOnly, getBaseValue, onTraverseIn, onShowTextInput]);

  const textInputButtons = React.useMemo(() => {
    return (
      <Tooltip title="Cancel">
        <span>
          <ActionButton
            aria-label="Cancel"
            color="inherit"
            onClick={() => {
              setDraftTextValue(textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "");
              setShowTextInput(false);
              onHideTextInput?.();
            }}
          >
            <CloseIcon />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [textRedline, onHideTextInput]);

  const actionButtons = React.useMemo(() => {
    return (
      <>
        {!showTextInput && !readOnly && (
          <>
            {acceptRejectButtons}
            {undoButton}
            {editButton}
          </>
        )}
        {showTextInput && textInputButtons}
        {!hideFieldHistory && (
          <ProposalFieldHistoryButton
            field={textRedline.field}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            hideBadge={hideHistoryBadge}
          />
        )}
      </>
    );
  }, [
    showTextInput,
    readOnly,
    textRedline,
    hideFieldHistory,
    hideHistoryBadge,
    negotiationId,
    viewerScope,
    acceptRejectButtons,
    undoButton,
    editButton,
    textInputButtons
  ]);

  useEffect(() => {
    onActionButtonsChange?.(actionButtons);
  }, [onActionButtonsChange, actionButtons]);

  const handleOpenTextInput = useCallback(() => {
    if (readOnly) return;
    setDraftTextValue(getBaseValue());
    setShowTextInput(true);
    onTraverseIn?.();
    onShowTextInput?.();
  }, [getBaseValue, onShowTextInput, onTraverseIn, readOnly]);

  const resolvedTextChanges = React.useMemo(() => {
    if (!textRedline.isResolved || !textRedline.textChanges?.length) return undefined;
    return textRedline.textChanges;
  }, [textRedline]);
  const showAcceptedResolvedAnimation = Boolean(
    animateAcceptedResolved &&
    textRedline.isResolved &&
    textRedline.isAccepted &&
    textRedline.wasRevised &&
    textRedline.currentEntry !== null &&
    resolvedTextChanges
  );
  const showResolvedDiff = Boolean(
    resolvedTextChanges &&
    (!textRedline.isAccepted || !textRedline.wasRevised || showAcceptedResolvedAnimation || textRedline.currentEntry === null)
  );
  const showResolvedCurrentValue = Boolean(
    textRedline.isResolved &&
    textRedline.currentEntry &&
    (!resolvedTextChanges || (textRedline.isAccepted && textRedline.wasRevised && !showAcceptedResolvedAnimation))
  );

  return (
    <TextContainer bold={bold} disableFullWidth={disableFullWidth} showTextInput={showTextInput}>
      <ProposalIncomingChangeHighlight field={textRedline.field} negotiationId={negotiationId} />
      <InputContainer className={className}>
        {!showTextInput && !textRedline.isResolved && textRedline.incomingAuthor && (
          <ResolutionTargetAuthorLabel author={textRedline.incomingAuthor} />
        )}
        {!showTextInput && !textRedline.textChanges && (
          <Icon>remove</Icon>
        )}
        {!showTextInput && showResolvedDiff && resolvedTextChanges && (
          <DiffText
            onClick={handleOpenTextInput}
            textChanges={resolvedTextChanges}
            allResolved={true}
            acceptedResolved={textRedline.isAccepted && textRedline.wasRevised}
            animateAcceptedResolved={animateAcceptedResolved}
          />
        )}
        {!showTextInput && showResolvedCurrentValue && (
          <ResolvedValue
            onClick={handleOpenTextInput}
          >
            {textRedline.currentEntry?.value}
          </ResolvedValue>
        )}
        {!showTextInput && !textRedline.isResolved && textRedline.textChanges && (
          <DiffText
            onClick={handleOpenTextInput}
            textChanges={textRedline.textChanges}
            allResolved={textRedline.isResolved}
          />
        )}
        {showTextInput && (
          <>
            {/* No "previously:" / "your changes:" preview above the input: the Comments/Changes pane
                carries the field's history, and repeating it beside every editor spent the room the
                editor itself needed and made one text field look unlike the next. */}
            <FormControl variant="standard" focused={focused}>
              <InputField
                multiline
                inputRef={forwardedRef}
                show={true}
                placeholder="Enter new text"
                error={issues && issues.length > 0}
                value={draftTextValue}
                onChange={(event) => {
                  try {
                    setDraftTextValue(event.target.value);
                  } catch (error) {
                    console.error("Error updating text", error);
                    enqueueSnackbar("Error updating text", { variant: "error" });
                  }
                }}
                onBlurCapture={(event: React.FocusEvent<HTMLInputElement>) => {
                  const nextFocus = event.relatedTarget as HTMLElement;
                  if (nextFocus && outerRef.current?.contains(nextFocus)) {
                    // Clicked within the same component
                    return;
                  }
                  try {
                    if (draftTextValue !== getBaseValue()) {
                      onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                    }
                    setShowTextInput(false);
                    // Guarded rather than `?.()`: the React Compiler cannot lower an optional call
                    // inside try/catch, and an absent callback was already a no-op.
                    if (onHideTextInput) {
                      onHideTextInput();
                    }
                  } catch (error) {
                    console.error("Error updating text", error);
                    enqueueSnackbar("Error updating text", {
                      variant: "error",
                    });
                  }
                }}
                onKeyDown={(event) => {
                  if (["Enter", "Tab"].includes(event.key) && draftTextValue !== "") {
                    try {
                      onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                      setShowTextInput(false);
                      // Guarded rather than `?.()`: the React Compiler cannot lower an optional call
                      // inside try/catch, and an absent callback was already a no-op.
                      if (onHideTextInput) {
                        onHideTextInput();
                      }
                    } catch (error) {
                      console.error("Error updating text", error);
                      enqueueSnackbar("Error updating text", {
                        variant: "error",
                      });
                    }
                  }
                  if (event.key === KeyboardKey.Escape) {
                    try {
                      setShowTextInput(false);
                      // Guarded rather than `?.()`: the React Compiler cannot lower an optional call
                      // inside try/catch, and an absent callback was already a no-op.
                      if (onHideTextInput) {
                        onHideTextInput();
                      }
                    } catch (error) {
                      console.error("Error updating text", error);
                      enqueueSnackbar("Error updating text", {
                        variant: "error",
                      });
                    }
                  }
                }}
                onBlur={onBlur}
                onFocus={onFocus}
              />
            </FormControl>
          </>
        )}
      </InputContainer>
    </TextContainer>
  );
});

export interface RedlineTextInputProps<T extends Text> extends BaseTextFieldProps {
  textRedline: FieldRedline<T>;
  issues?: ProposalIssue[];
  showTextInput?: boolean;
  showResolvedHelperText?: boolean;
  readOnly?: boolean;
  hideUndoButton?: boolean;
  hideAcceptRejectButtons?: boolean;
  hideLabel?: boolean;
  hideFieldHistory?: boolean;
  /**
   * Suppresses the embedded `ProposalFieldHistoryButton`'s own change-badge bubbles. Set by a caller
   * that already renders a `ProposalFieldChangeBadge` on this field's label/wrapper, so the same
   * unread/collision signal is never shown twice — see `ProposalFieldHistoryButtonProps.hideBadge`.
   */
  hideHistoryBadge?: boolean;
  lockAdornment?: React.ReactNode;
  negotiationId?: string;
  viewerScope?: string;
  showPlaceholder?: boolean;
  bold?: boolean;
  disableFullWidth?: boolean;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => T;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const TextRedlineInput = <T extends Text>(props: Readonly<RedlineTextInputProps<T>>) => {
  const {
    disabled,
    textRedline,
    issues,
    showResolvedHelperText: showResolvedHelperTextProp,
    hideUndoButton,
    hideAcceptRejectButtons,
    readOnly,
    hideLabel,
    hideFieldHistory,
    hideHistoryBadge,
    lockAdornment,
    variant,
    margin,
    negotiationId,
    viewerScope,
    showPlaceholder,
    bold,
    disableFullWidth,
    focused,
    fieldConstructor,
    onTextFieldRedlineChange,
    onTraverseIn,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;
  const showResolvedHelperText = showResolvedHelperTextProp ?? true;

  const [actionButtons, setActionButtons] = React.useState<JSX.Element>();
  const hasAcceptedResolvedChange = textRedline.isResolved && textRedline.isAccepted && textRedline.wasRevised;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const helperText =
    hasAcceptedResolvedChange && showResolvedHelperText ? "Resolved" : issues?.map((entry) => entry.description).join("\n");

  const outerRef = React.useRef<HTMLDivElement>(null);
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  const handleActionButtonsChange = useCallback((actionButtons: JSX.Element) => {
    setActionButtons(prev => (prev === actionButtons ? prev : actionButtons));
  }, []);

  const inputProps = React.useMemo<Partial<InputProps>>(() => {
    const baseProps: Partial<InputProps> = {
      inputComponent: InputSubComponent,
      inputProps: {
        readOnly,
        hideUndoButton,
        hideAcceptRejectButtons,
        textRedline,
        issues,
        hideFieldHistory,
        hideHistoryBadge,
        negotiationId,
        viewerScope,
        focused,
        showPlaceholder,
        bold,
        disableFullWidth,
        outerRef,
        onTraverseIn,
        onTextFieldRedlineChange,
        fieldConstructor,
        onActionButtonsChange: handleActionButtonsChange,
        onFocus,
        onBlur,
        onShowTextInput,
        onHideTextInput,
        animateAcceptedResolved,
        onAcceptedResolvedAnimationStart: triggerAcceptedResolvedAnimation,
      },
      endAdornment: actionButtons,
      startAdornment: lockAdornment,
    };
    return (variant && variant !== "outlined")
      ? { ...baseProps, disableUnderline: true }
      : baseProps;
  }, [
    readOnly,
      hideUndoButton,
      hideAcceptRejectButtons,
    textRedline,
    issues,
    hideFieldHistory,
    hideHistoryBadge,
    lockAdornment,
    negotiationId,
    viewerScope,
    focused,
    showPlaceholder,
    bold,
    disableFullWidth,
    outerRef,
    onTraverseIn,
    onTextFieldRedlineChange,
    fieldConstructor,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    animateAcceptedResolved,
    actionButtons,
    variant,
    handleActionButtonsChange,
    triggerAcceptedResolvedAnimation,
  ]);

  return (
    <RedlineTextBox
      {...otherProps}
      disabled={disabled}
      ref={outerRef}
      focused={focused}
      traversable={Boolean(onTraverseIn)}
      error={Boolean(issues?.length)}
      helperText={helperText}
      label={hideLabel ? undefined : textRedline.label}
      variant={variant ?? "outlined"}
      margin={margin ?? "normal"}
      sx={{
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(green[500], 0.06),
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: green[500],
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: green[600],
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
        }),
        ...(lockAdornment && {
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
            color: "warning.main",
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
        }),
      }}
      slotProps={{
        input: inputProps,
        inputLabel: { shrink: true },
      }}
      onFocus={onTraverseIn}
    />
  );
};

export default TextRedlineInput;
